import { configureStore, combineReducers } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import { persistReducer } from 'redux-persist';
import modelReducer, { IModelReducerState } from './Model/reducers/model';
import tableModalReducer, {
  ITableModalReducerState,
} from './TableModal/reducers/tableModal';
import commonReducer, { ICommonReducerState } from './Common/reducers/common';
import shoppingCartReducer, {
  IShoppingCartReducerState,
} from './ShoppingCart/reducers/shoppingCart';
import submitConfigurationReducer, {
  ISubmitConfigurationReducerState,
} from './SubmitConfiguration/reducers/submitConfiguration';
import createTransform from 'redux-persist/es/createTransform';
import formReducer, { IFormReducerState } from './Form/reducers/form';
import userReducer, { IUserReducerState } from './User/reducers/user';
import filtersReducer, {
  IFiltersReducerState,
} from './Filters/reducers/filters';
import colorModalReducer, {
  IColorModalReducerState,
} from './ColorModal/reducers/colorModal';
import orderReducer, { IOrderReducerState } from './Order/reducers/order';
import quotationOrderReducer, {
  IQuotationOrderReducerState,
} from './QuotationOrder/reducers/quotationOrder';
import { OrderTabsEnum } from 'types/Order.types';

const storeTransform = createTransform(
  // Transform the incoming state, persisting only the desired part
  (
    inboundState:
      | IModelReducerState
      | ICommonReducerState
      | IFormReducerState
      | IShoppingCartReducerState
      | ISubmitConfigurationReducerState
      | IUserReducerState
      | IFiltersReducerState
      | ITableModalReducerState
      | IColorModalReducerState
      | IOrderReducerState
      | IQuotationOrderReducerState
      | any,
    key
  ) => {
    if (key === 'modelInfo') {
      return {
        ...inboundState,
        models: null,
        questions: [],
        variables: null,
        images: null,
        pricing: null,
        roundPricing: false,
        bomItems: null,
        queryStatus: {
          attempt: false,
          success: false,
          error: null,
        },
        lang: 'en',
        extraPriceQuestions: [],
        connLeadMpr: null,
        connLeadTime: null,
        predefinedPricing: null,
      };
    } else if (key === 'commonInfo') {
      return {
        ...inboundState,
        isLoadingScreenShown: false,
        queryParamsProcessed: false,
        editConfigurationValidationWarnings: [],
      };
    } else if (key === 'formInfo') {
      return {
        ...inboundState,
        isFormValid: inboundState.isFormValid
          ? inboundState.isFormValid
          : false,
        areAllFieldsTouched: false,
        touchedQuestionNames: [],
        manuallyChangedQuestionNames: [],
        manuallyClearedQuestionNames: [],
        modelNotFound: false,
        questionPrices: [],
        includeBomItems: false,
      };
    } else if (key === 'shoppingCartInfo') {
      return {
        ...inboundState,
        isShoppingCartMode: true,
        triggerAddToShoppingCart: false,
        triggerShowAddedToCartModal: false,
        currentlyEditingShoppingCartLineId: null,
        currentlyEditingShoppingCartLineQuantity: null,
        triggerShowCartItemEditedModal: false,
        triggerEditCartItem: false,
        triggerUpdateQuotationLine: false,
        maxConnLeadTime: 0,
        shoppingCartId: null,
        quotationCartId: null,
        cartLength: 0,
        currency: null,
      };
    } else if (key === 'submitConfigurationInfo') {
      return {
        ...inboundState,
        intentUUID: null,
        triggerSubmitConfiguration: false,
        getEditConfigurationStatus: {
          attempt: false,
          success: false,
          error: false,
        },
        selectedConfigurationAnswers: [],
        clearedExtraPriceQuestionSlugs: [],
        triggerSaveTemplate: null,
        // Transient: a rehydrated id would apply a template to whatever
        // configuration happens to be open next.
        templateToApply: null,
        selectedTemplate: null,
        // Transient: they describe a submission of the page that was open.
        submissionIsSlow: false,
        submissionBeingChecked: null,
      };
    } else if (key === 'userInfo') {
      return {
        ...inboundState,
        intent: null,
      };
    } else if (key === 'colorModalInfo') {
      return {
        isOpen: true,
        question: null,
      };
    } else if (key === 'orderInfo') {
      return {
        ...inboundState,
        activeTab: OrderTabsEnum.Company,
        companyId: '',
        productId: '',
      };
    } else if (key === 'quotationOrderInfo') {
      // Entirely per-session: a rehydrated selection would belong to whichever
      // quotation was open last.
      return {
        activeTab: OrderTabsEnum.Products,
        selectedLineIds: [],
        detailsOrderDataBySupplier: {},
      };
    }
    return inboundState;
  },
  // Rehydration (outbound) transform
  (outboundState: any, key) => {
    if (key === 'modelInfo') {
      // resetDiscountLines is a Set, but redux-persist serializes it to a plain
      // `{}` (a Set has no enumerable keys). On rehydration that `{}` is not
      // iterable, so `new Set(resetDiscountLines)` in resetDiscountsForChangedLines
      // throws and the failed query wipes questions/pricing, reverting the form.
      // Restore a real (empty) Set — this tracking is per-session anyway.
      return {
        ...outboundState,
        resetDiscountLines: new Set<string>(),
      };
    }
    return outboundState;
  }
);

const persistConfig = {
  key: 'root',
  storage,
  transforms: [storeTransform],
};

const rootReducer = combineReducers({
  modelInfo: modelReducer,
  tableModalInfo: tableModalReducer,
  colorModalInfo: colorModalReducer,
  commonInfo: commonReducer,
  shoppingCartInfo: shoppingCartReducer,
  submitConfigurationInfo: submitConfigurationReducer,
  formInfo: formReducer,
  userInfo: userReducer,
  filtersInfo: filtersReducer,
  orderInfo: orderReducer,
  quotationOrderInfo: quotationOrderReducer,
});
export type IRootReducerState = ReturnType<typeof rootReducer>;

const persistedReducer = persistReducer(persistConfig, rootReducer);
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
